import { type FormEvent, useEffect, useState } from "react";

import { completePasswordRecovery, PasswordRecoveryError, requestPasswordRecovery } from "./api";
import { useAuthCopy } from "./authCatalog";

function Brand() {
  const { t } = useAuthCopy();
  return <span className="login-brand"><span className="login-brand-mark">K</span><span><strong>Kavoosh</strong><small>{t("brand.tagline")}</small></span></span>;
}

type RecoveryState =
  | { kind: "idle" | "submitting" }
  | { kind: "error" | "sent" | "updated"; message: string };

export function PasswordRecoveryPage({ token, onBackToLogin }: {
  token?: string | undefined;
  onBackToLogin: () => void;
}) {
  const { t } = useAuthCopy();
  const [state, setState] = useState<RecoveryState>({ kind: "idle" });
  const confirmationMode = Boolean(token);

  useEffect(() => {
    document.title = t("recovery.documentTitle");
  }, [t]);

  async function request(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = new FormData(form);
    const email = String(fields.get("email") ?? "").trim();
    setState({ kind: "submitting" });
    try {
      await requestPasswordRecovery(email);
      form.reset();
      setState({
        kind: "sent",
        message: t("recovery.sent"),
      });
    } catch (error) {
      setState({
        kind: "error",
        message: error instanceof PasswordRecoveryError
          ? error.message
          : t("recovery.startError"),
      });
    }
  }

  async function confirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = new FormData(form);
    const newPassword = String(fields.get("new_password") ?? "");
    const confirmation = String(fields.get("confirmation") ?? "");
    if (newPassword !== confirmation) {
      setState({ kind: "error", message: t("recovery.mismatch") });
      return;
    }
    setState({ kind: "submitting" });
    try {
      await completePasswordRecovery({ token: token ?? "", newPassword });
      form.reset();
      setState({ kind: "updated", message: t("recovery.updated") });
    } catch (error) {
      form.reset();
      setState({
        kind: "error",
        message: error instanceof PasswordRecoveryError
          ? error.message
          : t("recovery.invalid"),
      });
    }
  }

  const finished = state.kind === "sent" || state.kind === "updated";

  return (
    <main className="login-page">
      <aside className="login-aside">
        <button className="login-brand-button" type="button" onClick={onBackToLogin} aria-label={t("recovery.back")}><Brand /></button>
        <div className="login-aside-copy"><span className="login-eyebrow">{t("recovery.eyebrow")}</span><h1>{t("recovery.asideTitleLine1")}<br />{t("recovery.asideTitleLine2")}</h1><p>{t("recovery.asideBody")}</p></div>
        <div className="login-principles"><span>◇ {t("recovery.principleExpiry")}</span><span>◈ {t("recovery.principleSingleUse")}</span><span>✓ {t("recovery.principleSessions")}</span></div>
      </aside>
      <section className="login-main" aria-labelledby="recovery-title">
        <div className="login-top-actions"><button className="login-back" type="button" onClick={onBackToLogin}>← {t("recovery.back")}</button></div>
        <div className="login-card">
          <div className="login-mobile-brand"><Brand /></div>
          <span className="login-lock" aria-hidden="true">{confirmationMode ? "⌾" : "↻"}</span>
          <h2 id="recovery-title">{confirmationMode ? t("recovery.newTitle") : t("recovery.resetTitle")}</h2>
          <p className="login-intro">{confirmationMode ? t("recovery.newIntro") : t("recovery.resetIntro")}</p>

          {(state.kind === "error" || state.kind === "sent" || state.kind === "updated") && (
            <div className={`login-alert ${state.kind === "error" ? "error" : "success"}`} role={state.kind === "error" ? "alert" : "status"}>{state.message}</div>
          )}

          {!finished && !confirmationMode && (
            <form onSubmit={request} aria-busy={state.kind === "submitting"}>
              <fieldset disabled={state.kind === "submitting"}>
                <label htmlFor="recovery-email">{t("recovery.email")}</label>
                <input id="recovery-email" name="email" type="email" autoComplete="email" required maxLength={254} placeholder={t("recovery.emailPlaceholder")} />
                <button className="login-primary" type="submit">{state.kind === "submitting" ? t("recovery.sending") : t("recovery.send")}</button>
              </fieldset>
            </form>
          )}

          {!finished && confirmationMode && (
            <form onSubmit={confirm} aria-busy={state.kind === "submitting"}>
              <fieldset disabled={state.kind === "submitting"}>
                <label htmlFor="recovery-password">{t("recovery.newPassword")}</label>
                <input id="recovery-password" name="new_password" type="password" autoComplete="new-password" required minLength={14} maxLength={1024} placeholder={t("recovery.passwordPlaceholder")} />
                <label htmlFor="recovery-confirmation">{t("recovery.confirmPassword")}</label>
                <input id="recovery-confirmation" name="confirmation" type="password" autoComplete="new-password" required minLength={14} maxLength={1024} placeholder={t("recovery.confirmPlaceholder")} />
                <button className="login-primary" type="submit">{state.kind === "submitting" ? t("recovery.updating") : t("recovery.update")}</button>
              </fieldset>
            </form>
          )}

          {!confirmationMode && !finished && <p className="login-recovery-note">{t("recovery.neutralNotice")}</p>}
          {finished && <button className="login-primary" type="button" onClick={onBackToLogin}>{t("recovery.back")}</button>}
        </div>
        <div className="login-footer">{t("login.footer")}</div>
      </section>
    </main>
  );
}
